'use client';

import Link from 'next/link';
import { useNav } from '@/components/NavProvider';
import { CookiePreferencesLink } from '@/components/CookiePreferencesLink';
import { SOCIAL_ICONS, type SocialIconName } from '@/components/social-icons';
import SummarizeWithAi from '@/components/SummarizeWithAi';
import { AI_INFO_LABEL } from '@/lib/ai-providers';
import type { SocialSettings } from '@/lib/nav';

const SOCIAL_DEFAULTS: Record<string, string> = {
  Facebook: 'https://www.facebook.com/OfficePortalHRMS',
  // x.com is the canonical Twitter/X domain since the rebrand. NOTE: the CMS
  // value (Site Settings → Social Links) wins over this default — if a
  // twitter.com URL is saved there, update it in the admin too.
  Twitter: 'https://x.com/officeportalhrm',
  LinkedIn: 'https://www.linkedin.com/showcase/officeportal',
  YouTube: 'https://www.youtube.com/channel/UCnNkBcdPCKk0Jh5dbBlkgDw',
};

// Shared by the two secondary links in the bottom row so they always match.
// text-white/60, not /40: at 14px, /40 on the dark footer is ~3.4:1 — below
// the 4.5:1 WCAG AA minimum for text. (The copyright line is not a link and
// keeps /40.)
const SECONDARY_LINK = 'text-white/60 hover:text-t-accent text-sm transition-colors';

/** Resolve social links: CMS setting wins, else the hardcoded default; platforms with neither are omitted. */
export function socialLinksFromSettings(s: SocialSettings = {}): { label: SocialIconName; href: string }[] {
  const map: { label: SocialIconName; key: keyof SocialSettings }[] = [
    { label: 'Facebook', key: 'facebook' },
    { label: 'Twitter', key: 'twitter' },
    { label: 'LinkedIn', key: 'linkedin' },
    { label: 'YouTube', key: 'youtube' },
    { label: 'Instagram', key: 'instagram' },
  ];
  return map
    .map(({ label, key }) => ({ label, href: s[key] || SOCIAL_DEFAULTS[label] || '' }))
    .filter((l) => l.href);
}

/** `hideSummarize`: the 404 page opts out — usePathname() there is the missing URL, so the prompt would point an assistant at a 404. */
export default function Footer({ hideSummarize = false }: { hideSummarize?: boolean } = {}) {
  const { footerColumns, social, ai } = useNav();
  const socialLinks = socialLinksFromSettings(social).filter((l) => SOCIAL_ICONS[l.label]);
  return (
    <footer className="bg-t-primary-dark pt-16 pb-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Top: Logo + Link columns */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8 mb-12">
          {/* Brand + Contact */}
          <div className="col-span-2">
            <Link href="/" className="flex items-center gap-2 mb-4">
              <img
                src="/OfficePortal-white.svg"
                alt="OfficePortal"
                className="h-10"
              />
            </Link>
            <p className="text-white/50 text-sm leading-relaxed mb-4">
              All-in-one HRMS software to manage attendance, payroll, leave, performance and more.
            </p>
            <div className="space-y-2 text-sm text-white/50">
              <p>F3, Plot 218, Atrium Spring Meadows,<br />Chennai, India</p>
              <p>
                <a href="tel:+918925785888" className="hover:text-t-accent transition-colors">+91 89257 85888</a>
                {' · '}
                <a href="tel:+918454869085" className="hover:text-t-accent transition-colors">+91 84548 69085</a>
              </p>
              <p>
                <a href="mailto:sales@officeportal.io" className="hover:text-t-accent transition-colors">sales@officeportal.io</a>
              </p>
            </div>

            {/* App Store badges */}
            <div className="flex items-center gap-3 mt-5">
              <a
                href="https://play.google.com/store/apps/details?id=com.awarious.officeportal"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 transition-colors border border-white/10"
              >
                <svg className="w-4 h-4" viewBox="0 0 512 512" fill="none">
                  <path d="M325.3 234.3L104.6 13l280.8 161.2-60.1 60.1z" fill="#EA4335"/>
                  <path d="M47 0C34 6.8 25.3 19.2 25.3 35.3v441.3c0 16.1 8.7 28.5 21.7 35.3l256.6-256.6L47 0z" fill="#4285F4"/>
                  <path d="M447.4 243.1L385.4 209l-60.1 47 60.1 60.1 62-32.9c11.7-8.7 11.7-31.2 0-40.1z" fill="#FBBC04"/>
                  <path d="M104.6 499l280.8-161.2-60.1-60.1L104.6 499z" fill="#34A853"/>
                </svg>
                <span className="text-white/60 text-xs font-medium">Google Play</span>
              </a>
              <a
                href="https://apps.apple.com/fi/app/officeportal-hrms/id1570992542"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 transition-colors border border-white/10"
              >
                <svg className="w-4 h-4 text-white/60" viewBox="0 0 24 24" fill="currentColor"><path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" /></svg>
                <span className="text-white/60 text-xs font-medium">App Store</span>
              </a>
            </div>
          </div>

          {/* Link columns */}
          {footerColumns.map((column, i) => (
            <div key={column.heading || `col-${i}`}>
              {column.heading ? (
                <h4 className="text-white font-semibold text-sm mb-4">{column.heading}</h4>
              ) : (
                // No heading: reserve the same vertical space so this column's
                // links stay aligned with the link lists of columns that do have
                // a heading (otherwise they shift up into the heading row).
                <div className="text-sm mb-4" aria-hidden="true">&nbsp;</div>
              )}
              <ul className="space-y-2.5">
                {(column.links ?? []).map((link) => (
                  <li key={link.label}>
                    <Link
                      href={link.url || '#'}
                      target={link.openInNewTab ? '_blank' : undefined}
                      rel={link.openInNewTab ? 'noopener noreferrer' : undefined}
                      className="text-white/50 text-sm hover:text-t-accent transition-colors"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Summarize with AI — one icon link per enabled assistant (Site Settings → AI).
            Guarded here as well as inside the widget: otherwise the mb-8 wrapper
            renders as an empty gap when the widget returns null. */}
        {!hideSummarize && ai?.showSummarize && ai.providers.length > 0 && (
          <div className="flex justify-center sm:justify-end mb-8" data-testid="footer-ai-row">
            <SummarizeWithAi settings={ai} />
          </div>
        )}

        {/* Divider */}
        <div className="h-px bg-white/10 mb-8" />

        {/* Bottom: Copyright | secondary links | Social */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-white/40 text-sm" suppressHydrationWarning>
            &copy; {new Date().getFullYear()} OfficePortal. All rights reserved.
          </p>
          {/* One flex child for both secondary links: as direct children of the
              justify-between row they were pushed to opposite ends. Wrapped
              unconditionally so the row is always three groups. */}
          <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-6">
            <CookiePreferencesLink className={SECONDARY_LINK} />
            {ai?.infoHref &&
              (ai.infoHref === '/llms.txt' ? (
                // /llms.txt is a route handler, not a page: next/link would prefetch it as one.
                <a href={ai.infoHref} className={SECONDARY_LINK} data-testid="footer-ai-info">
                  {AI_INFO_LABEL}
                </a>
              ) : (
                <Link href={ai.infoHref} className={SECONDARY_LINK} data-testid="footer-ai-info">
                  {AI_INFO_LABEL}
                </Link>
              ))}
          </div>
          <div className="flex items-center gap-4">
            {socialLinks.map((s) => (
              <a
                key={s.label}
                href={s.href}
                target="_blank"
                rel="noopener noreferrer"
                className="text-white/40 hover:text-t-accent transition-colors"
                aria-label={s.label}
              >
                {SOCIAL_ICONS[s.label]}
              </a>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}
